import { useEffect, useRef } from 'react'

/**
 * Hook to manage silence.mp3 audio playback to keep connection alive
 * when browser tab loses focus (workaround for mobile browsers)
 *
 * Based on legacy chat.js implementation that plays silence.mp3 in the background
 * to prevent browsers from throttling WebRTC connections when tab is backgrounded.
 *
 * @param {Object} options
 * @param {string} options.chatStatus - Current chat status
 * @param {string} options.chatMode - Current chat mode (video, audio, text)
 * @returns {Object} - Ref to the audio element
 */
export const useSilenceAudio = ({ chatStatus, chatMode }) => {
  const audioRef = useRef(null)
  const shouldPlayRef = useRef(false)

  // Determine if we should use silence audio
  // Only for Safari (including mobile Safari) and when chat is active
  const isSafari =
    navigator.userAgent.includes('Safari') &&
    !navigator.userAgent.includes('Chrome')
  const isActiveChat =
    chatStatus === 'chat_active' &&
    (chatMode === 'video' || chatMode === 'audio')
  const shouldUseSilenceAudio = isSafari && isActiveChat

  // Create and manage the audio element
  useEffect(() => {
    if (!shouldUseSilenceAudio) {
      // Clean up if we shouldn't use it
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      return
    }

    // Check if audio element already exists (might be created by another component)
    let audio = document.getElementById('loopAudio')
    if (!audio) {
      // Create audio element if it doesn't exist
      audio = document.createElement('audio')
      audio.id = 'loopAudio'
      audio.autoplay = true
      audio.loop = true
      audio.playsInline = true
      audio.src = 'https://yns-com.s3.amazonaws.com/images/chat/silence.mp3'
      audio.style.display = 'none'
      document.body.appendChild(audio)
    }
    audioRef.current = audio
    shouldPlayRef.current = true

    // Try to play the audio
    const playAudio = () => {
      if (audio && shouldPlayRef.current) {
        audio
          .play()
          .then(() => {
            // console.log('Silence audio started playing')
            return null
          })
          .catch((error) => {
            console.warn('Could not play silence audio:', error)
          })
      }
    }

    // Initial play attempt
    playAudio()

    // Handle visibility changes to keep audio playing when tab loses focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldPlayRef.current) {
        // Tab became visible - ensure audio is still playing
        playAudio()
      }
      // When tab becomes hidden, audio should continue playing
      // (this is the whole point - to keep connection alive when backgrounded)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      shouldPlayRef.current = false
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
    }
  }, [chatStatus, chatMode, shouldUseSilenceAudio])

  // Stop audio when chat ends or becomes inactive
  useEffect(() => {
    if (
      audioRef.current &&
      (chatStatus === 'chat_ended' ||
        chatStatus === 'call_cancelled' ||
        chatStatus === 'call_missed' ||
        !shouldUseSilenceAudio)
    ) {
      shouldPlayRef.current = false
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
  }, [chatStatus, chatMode, shouldUseSilenceAudio])

  return audioRef
}
